import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import firebase from "firebase/app";
import "firebase/firestore";
import Notiflix from "notiflix";
import logo from "../assets/logo.png";
import cafeOrderingImg from "../assets/atTheBar.png";
import outdoorServiceImg from "../assets/wheelchairStairs.png";
import venueAccessibilityImg from "../assets/5.png";
import parkingAccessibilityImg from "../assets/disabledParking.png";
import highTablesImg from "../assets/venue accessible.svg";
import cafeFriendsImg from "../assets/handPhone.png";
import carersOutingImg from "../assets/accessibility requirement.svg";
import browseOrderImg from "../assets/9.png";
import cafeAccessibleImg from "../assets/cafe accessible.svg";
import checkFirstImg from "../assets/Artwork-1781.png";
import googlePlayButton from "../assets/Google Play App Button.png";
import appStoreButton from "../assets/Apple App Store Button.png";
import SEO from "../components/SEO";

export const Landing = () => {
  const history = useHistory();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [backToTopVisible, setBackToTopVisible] = useState(false);
  const [textSizeIndex, setTextSizeIndex] = useState(0);
  const [isReading, setIsReading] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [cursorGlowActive, setCursorGlowActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [a11yAnnouncement, setA11yAnnouncement] = useState('');
  const [formFeedback, setFormFeedback] = useState({ message: '', type: '' });

  const heroRef = useRef(null);
  const mainContentRef = useRef(null);
  const menuButtonRef = useRef(null);
  const speechSynthesisRef = useRef(null);
  const readIndexRef = useRef(0);
  const isReadingRef = useRef(false);

  const textSizes = ['default', 'large', 'larger', 'largest'];
  const prefersReducedMotion = typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  const announceToScreenReader = (message) => {
    setA11yAnnouncement('');
    setTimeout(() => setA11yAnnouncement(message), 50);
  };

  // Scroll Progress Bar
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      const progressBar = document.getElementById('scroll-progress');
      if (progressBar) {
        progressBar.style.width = scrollPercent + '%';
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Header Scroll & Back to Top
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setHeaderScrolled(!entry.isIntersecting);
          setBackToTopVisible(!entry.isIntersecting);
        });
      },
      { threshold: 0, rootMargin: '-100px 0px 0px 0px' }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => {
      if (heroRef.current) {
        observer.unobserve(heroRef.current);
      }
    };
  }, []);

  // Scroll Reveal Animations
  useEffect(() => {
    if (prefersReducedMotion) {
      document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale').forEach(el => {
        el.classList.add('revealed');
      });
      return;
    }

    const revealElements = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('revealed');
            }, index * 50);
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    revealElements.forEach(el => revealObserver.observe(el));

    return () => revealObserver.disconnect();
  }, [prefersReducedMotion]);

  // Parallax Effect
  useEffect(() => {
    if (prefersReducedMotion) return;

    const handleParallax = () => {
      const parallaxElements = document.querySelectorAll('.parallax-element');

      parallaxElements.forEach(el => {
        const speed = parseFloat(el.dataset.speed) || 0.05;
        const rect = el.getBoundingClientRect();
        const centerY = rect.top + rect.height / 2;
        const viewportCenter = window.innerHeight / 2;
        const offset = (centerY - viewportCenter) * speed;

        el.style.transform = `translateY(${offset}px)`;
        el.style.willChange = 'transform';
      });
    };

    handleParallax(); // Initial call
    window.addEventListener('scroll', handleParallax, { passive: true });
    return () => window.removeEventListener('scroll', handleParallax);
  }, [prefersReducedMotion]);

  // 3D Tilt Cards
  useEffect(() => {
    if (prefersReducedMotion) return;

    const tiltCards = document.querySelectorAll('[data-tilt]');

    const handleMouseMove = (card) => (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const rotateX = (y - centerY) / 20;
      const rotateY = (centerX - x) / 20;

      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
    };

    const handleMouseLeave = (card) => () => {
      card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale3d(1, 1, 1)';
    };

    tiltCards.forEach(card => {
      const moveHandler = handleMouseMove(card);
      const leaveHandler = handleMouseLeave(card);
      const focusHandler = () => {
        card.style.transform = 'perspective(1000px) rotateX(-2deg) rotateY(0deg) scale3d(1.02, 1.02, 1.02)';
      };
      const blurHandler = () => {
        card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale3d(1, 1, 1)';
      };
      card.addEventListener('mousemove', moveHandler);
      card.addEventListener('mouseleave', leaveHandler);
      card.addEventListener('focus', focusHandler);
      card.addEventListener('blur', blurHandler);

      card._moveHandler = moveHandler;
      card._leaveHandler = leaveHandler;
      card._focusHandler = focusHandler;
      card._blurHandler = blurHandler;
    });

    return () => {
      tiltCards.forEach(card => {
        if (card._moveHandler) card.removeEventListener('mousemove', card._moveHandler);
        if (card._leaveHandler) card.removeEventListener('mouseleave', card._leaveHandler);
        if (card._focusHandler) card.removeEventListener('focus', card._focusHandler);
        if (card._blurHandler) card.removeEventListener('blur', card._blurHandler);
      });
    };
  }, [prefersReducedMotion]);

  // Magnetic Buttons
  useEffect(() => {
    if (prefersReducedMotion) return;

    const magneticBtns = document.querySelectorAll('.magnetic-btn');

    magneticBtns.forEach(btn => {
      const parent = btn.parentElement;

      const moveHandler = (e) => {
        const rect = parent.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;

        btn.style.transform = `translate(${x * 0.3}px, ${y * 0.3}px)`;
      };

      const leaveHandler = () => {
        btn.style.transform = 'translate(0, 0)';
      };

      const focusHandler = () => {
        btn.style.transform = 'translate(0, -3px)';
      };
      const blurHandler = () => {
        btn.style.transform = 'translate(0, 0)';
      };

      parent.addEventListener('mousemove', moveHandler);
      parent.addEventListener('mouseleave', leaveHandler);
      btn.addEventListener('focus', focusHandler);
      btn.addEventListener('blur', blurHandler);

      parent._magneticMove = moveHandler;
      parent._magneticLeave = leaveHandler;
      btn._magneticFocus = focusHandler;
      btn._magneticBlur = blurHandler;
    });

    return () => {
      const magneticBtns = document.querySelectorAll('.magnetic-btn');
      magneticBtns.forEach(btn => {
        const parent = btn.parentElement;
        if (parent._magneticMove) parent.removeEventListener('mousemove', parent._magneticMove);
        if (parent._magneticLeave) parent.removeEventListener('mouseleave', parent._magneticLeave);
        if (btn._magneticFocus) btn.removeEventListener('focus', btn._magneticFocus);
        if (btn._magneticBlur) btn.removeEventListener('blur', btn._magneticBlur);
      });
    };
  }, [prefersReducedMotion]);

  // Cursor Glow Effect
  useEffect(() => {
    if (prefersReducedMotion) return;

    const handleMouseMove = (e) => {
      setCursorPos({ x: e.clientX, y: e.clientY });

      const darkSections = document.querySelectorAll('.hero, .contact-section, .user-cta-section');
      let isInDarkSection = false;

      darkSections.forEach(section => {
        const rect = section.getBoundingClientRect();
        if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
          isInDarkSection = true;
        }
      });

      setCursorGlowActive(isInDarkSection);
    };

    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [prefersReducedMotion]);

  // Hero Particles
  useEffect(() => {
    if (prefersReducedMotion) return;

    const particlesContainer = document.getElementById('hero-particles');
    if (!particlesContainer) return;

    const particleCount = 20;

    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      particle.classList.add('hero-particle');
      particle.style.left = Math.random() * 100 + '%';
      particle.style.animationDelay = Math.random() * 20 + 's';
      particle.style.animationDuration = (15 + Math.random() * 10) + 's';
      particle.style.opacity = Math.random() * 0.5 + 0.2;
      particlesContainer.appendChild(particle);
    }

    return () => {
      if (particlesContainer) {
        particlesContainer.innerHTML = '';
      }
    };
  }, [prefersReducedMotion]);

  // Mobile Menu Toggle
  const toggleMobileMenu = () => {
    const opening = !mobileMenuOpen;
    setMobileMenuOpen(opening);
    if (opening) {
      setTimeout(() => {
        const firstLink = document.querySelector('#mobile-nav a');
        if (firstLink) firstLink.focus();
      }, 100);
    }
  };

  // Mobile Menu Escape Key & Focus Trap
  useEffect(() => {
    if (!mobileMenuOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
        return;
      }

      // Focus trap within mobile menu
      if (e.key === 'Tab') {
        const mobileNav = document.getElementById('mobile-nav');
        if (!mobileNav) return;
        const focusable = mobileNav.querySelectorAll('a, button');
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  // Back to Top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  };

  // Text Size Toggle
  const toggleTextSize = () => {
    console.log('Toggle text size clicked, current index:', textSizeIndex);

    const newIndex = (textSizeIndex + 1) % textSizes.length;
    const newSize = textSizes[newIndex];
    console.log('New index:', newIndex, 'New size:', newSize);

    setTextSizeIndex(newIndex);

    // Map size names to pixel values
    const sizeMap = {
      'default': '20px',
      'large': '22px',
      'larger': '24px',
      'largest': '26px'
    };

    // Directly set the font-size on the html element
    document.documentElement.style.fontSize = sizeMap[newSize];
    console.log('Set font-size to:', sizeMap[newSize]);

    // Also set the CSS variable in case other styles depend on it
    document.documentElement.style.setProperty('--font-size-base', sizeMap[newSize]);

    // Update class for any CSS that might depend on it
    textSizes.forEach(size => {
      if (size !== 'default') {
        document.documentElement.classList.remove(`text-size-${size}`);
      }
    });
    if (newSize !== 'default') {
      document.documentElement.classList.add(`text-size-${newSize}`);
    }

    announceToScreenReader(`Text size changed to ${newSize}`);
    localStorage.setItem('piatto-text-size', newSize);
    console.log('Computed font size on html:', getComputedStyle(document.documentElement).fontSize);
  };

  // Load saved text size
  useEffect(() => {
    const savedSize = localStorage.getItem('piatto-text-size');
    if (savedSize) {
      const index = textSizes.indexOf(savedSize);
      if (index !== -1) {
        setTextSizeIndex(index);

        // Map size names to pixel values
        const sizeMap = {
          'default': '20px',
          'large': '22px',
          'larger': '24px',
          'largest': '26px'
        };

        // Directly set the font-size on the html element
        document.documentElement.style.fontSize = sizeMap[savedSize];

        // Also set the CSS variable
        document.documentElement.style.setProperty('--font-size-base', sizeMap[savedSize]);

        // Also add class
        if (savedSize !== 'default') {
          document.documentElement.classList.add(`text-size-${savedSize}`);
        }

        console.log('Loaded saved text size:', savedSize, 'Font size:', sizeMap[savedSize]);
      }
    }
  }, [textSizes]);

  // Read Aloud
  const readNextElement = () => {
    console.log('readNextElement called, isReadingRef:', isReadingRef.current, 'index:', readIndexRef.current);

    if (!mainContentRef.current || !isReadingRef.current) {
      console.log('Stopping - no content or not reading');
      return;
    }

    const elements = mainContentRef.current.querySelectorAll('h1, h2, h3, p, li');
    console.log('Found elements:', elements.length);

    if (readIndexRef.current >= elements.length) {
      console.log('Reached end of elements');
      isReadingRef.current = false;
      setIsReading(false);
      document.querySelectorAll('.reading-highlight').forEach(el => el.classList.remove('reading-highlight'));
      return;
    }

    const element = elements[readIndexRef.current];
    const text = element.textContent.trim();
    console.log('Reading element:', element.tagName, 'text:', text.substring(0, 50));

    if (text && !element.closest('.image-placeholder')) {
      // Remove previous highlights
      document.querySelectorAll('.reading-highlight').forEach(el => {
        el.classList.remove('reading-highlight');
      });

      // Add highlight to current element
      element.classList.add('reading-highlight');
      console.log('Added reading-highlight class, classes:', element.className);

      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.onend = () => {
        console.log('Utterance ended');
        if (isReadingRef.current) {
          readIndexRef.current++;
          setTimeout(() => readNextElement(), 100); // Small delay between readings
        }
      };
      utterance.onerror = (event) => {
        console.error('Speech error:', event);
        if (isReadingRef.current) {
          readIndexRef.current++;
          readNextElement();
        }
      };
      window.speechSynthesis.speak(utterance);
      console.log('Started speaking');
    } else {
      console.log('Skipping element (empty or placeholder)');
      readIndexRef.current++;
      readNextElement();
    }
  };

  const toggleReadAloud = () => {
    console.log('Toggle read aloud clicked, currently reading:', isReadingRef.current);

    if (typeof window === 'undefined' || !window.speechSynthesis) {
      alert('Sorry, your browser does not support text-to-speech.');
      return;
    }

    if (isReadingRef.current) {
      console.log('Stopping speech...');
      // Cancel all speech
      window.speechSynthesis.cancel();
      // Force stop
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.pause();
        window.speechSynthesis.cancel();
      }
      isReadingRef.current = false;
      setIsReading(false);
      announceToScreenReader('Reading stopped');
      document.querySelectorAll('.reading-highlight').forEach(el => {
        el.classList.remove('reading-highlight');
        console.log('Removed highlight from:', el.tagName);
      });
      console.log('Speech stopped, isReading:', isReadingRef.current);
    } else {
      console.log('Starting speech...');
      isReadingRef.current = true;
      setIsReading(true);
      announceToScreenReader('Reading page content aloud');
      readIndexRef.current = 0;
      readNextElement();
    }
  };

  // Smooth Scroll for Anchor Links
  useEffect(() => {
    const handleAnchorClick = (e) => {
      const target = e.target.closest('a[href^="#"]');
      if (!target) return;

      const href = target.getAttribute('href');
      if (href === '#') return;

      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        // Calculate offset for sticky header - use larger offset to ensure titles are visible
        let headerOffset = 250; // Default large offset

        // Special handling for specific sections to target their titles
        if (href === '#contact') {
          const contactTitle = document.getElementById('contact-title');
          if (contactTitle) {
            const titlePosition = contactTitle.getBoundingClientRect().top;
            const offsetPosition = titlePosition + window.pageYOffset - 120;
            window.scrollTo({
              top: offsetPosition,
              behavior: prefersReducedMotion ? 'auto' : 'smooth'
            });
            setMobileMenuOpen(false);
            contactTitle.setAttribute('tabindex', '-1');
            setTimeout(() => {
              contactTitle.focus();
              contactTitle.addEventListener('blur', () => contactTitle.removeAttribute('tabindex'), { once: true });
            }, 500);
            return;
          }
        }

        // Check if element itself is a title (h2, h3, etc) that needs special offset
        if (element && (element.tagName === 'H2' || element.tagName === 'H3')) {
          const titlePosition = element.getBoundingClientRect().top;
          // Final offset for user-cta-title - shows heading with minimal white background
          const offset = element.id === 'user-cta-title' ? 238 : 120;
          const offsetPosition = titlePosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: prefersReducedMotion ? 'auto' : 'smooth'
          });
          setMobileMenuOpen(false);
          element.setAttribute('tabindex', '-1');
          setTimeout(() => {
            element.focus();
            element.addEventListener('blur', () => element.removeAttribute('tabindex'), { once: true });
          }, 500);
          return;
        }

        // Default scroll behavior for other anchors
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: prefersReducedMotion ? 'auto' : 'smooth'
        });

        // Close mobile menu if open
        setMobileMenuOpen(false);

        element.setAttribute('tabindex', '-1');
        setTimeout(() => {
          element.focus();
          element.addEventListener('blur', () => element.removeAttribute('tabindex'), { once: true });
        }, 500);
      }
    };

    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, [prefersReducedMotion]);

  // Smooth Scroll with Offset for Header
  const handleContactScroll = (e) => {
    e.preventDefault();
    const contactTitle = document.getElementById('contact-title');
    if (contactTitle) {
      const headerOffset = 400; // Large offset to ensure title is visible
      const elementPosition = contactTitle.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const handleDownloadAppScroll = (e) => {
    e.preventDefault();
    const userCtaTitle = document.getElementById('user-cta-title');
    if (userCtaTitle) {
      const headerOffset = 400; // Large offset to ensure title is visible
      const elementPosition = userCtaTitle.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Contact Form Handler
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    console.log('=== FORM SUBMISSION STARTED ===');

    if (isSubmitting) {
      console.log('Already submitting, returning...');
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData(e.target);
      const contactData = {
        firstName: formData.get('first-name'),
        lastName: formData.get('last-name'),
        email: formData.get('email'),
        phone: formData.get('phone') || '',
        interest: formData.get('interest'),
        message: formData.get('message'),
        submittedAt: firebase.firestore.FieldValue.serverTimestamp(),
        status: 'new'
      };

      console.log('Contact data to submit:', contactData);

      // Save to Firestore
      const docRef = await firebase.firestore().collection('ContactRequest').add(contactData);
      console.log('✅ SUCCESS! Document ID:', docRef.id);

      // Reset form first
      e.target.reset();
      console.log('Form reset complete');

      // Show success message and move focus to it
      setFormFeedback({ message: 'Message sent! Thank you for contacting us. We\'ll be in touch soon.', type: 'success' });
      setTimeout(() => {
        const feedback = document.querySelector('.form-feedback');
        if (feedback) { feedback.setAttribute('tabindex', '-1'); feedback.focus(); }
      }, 100);

    } catch (error) {
      console.error('❌ ERROR submitting contact form:', error);
      setFormFeedback({ message: `Submission failed: ${error.message}. Please try again or email us directly.`, type: 'error' });
      setTimeout(() => {
        const feedback = document.querySelector('.form-feedback');
        if (feedback) { feedback.setAttribute('tabindex', '-1'); feedback.focus(); }
      }, 100);
    } finally {
      setIsSubmitting(false);
      console.log('=== FORM SUBMISSION COMPLETE ===');
    }
  };

  return (
    <>
      <SEO
        title="Accessible Dining Platform"
        description="Piatto is Australia's accessible food directory. Browse menus, check venue accessibility for wheelchairs and disabilities, and order ahead. Free for venues. Available on iOS and Android."
        url="https://piatto.com.au"
        image="https://piatto.com.au/logo512.png"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Piatto",
            url: "https://piatto.com.au",
            logo: "https://piatto.com.au/logo512.png",
            description: "Australia's accessible food and beverage ordering platform. Browse menus, check venue accessibility, and order ahead — designed for people with disabilities, carers, and everyone.",
            sameAs: [
              "http://youtube.com/@piattoapp",
              "https://www.instagram.com/piattoit",
              "https://www.facebook.com/piattoAUS",
              "https://twitter.com/@PiattoNow",
              "https://piatto.substack.com/",
              "https://www.linkedin.com/company/piattoaus/",
              "https://discord.com/invite/6dcdt2d24x"
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "MobileApplication",
            name: "Piatto",
            operatingSystem: "iOS, Android",
            applicationCategory: "FoodAndDrinkApplication",
            offers: { "@type": "Offer", price: "0", priceCurrency: "AUD" },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How does Piatto help disabled diners?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Piatto allows users to browse menus, check venue accessibility, and order ahead without needing to enter venues, talk to staff, or use inaccessible QR codes. Our platform is designed to be fully accessible for vision-impaired, hearing-impaired, wheelchair users, and people with other disabilities.",
                },
              },
              {
                "@type": "Question",
                name: "Is Piatto free for venues?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes! Venue sign-up is completely free. Piatto helps venues reach 20% more customers who have accessibility needs, including elderly people, mothers with prams, temporarily injured individuals, and carers.",
                },
              },
              {
                "@type": "Question",
                name: "What accessibility features does Piatto offer?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Piatto offers screen reader compatibility, adjustable text sizes, keyboard navigation, high contrast mode, and skip links. Our platform works without requiring QR codes, phone calls, or physical menus, making dining accessible for everyone.",
                },
              },
              {
                "@type": "Question",
                name: "Which cities in Australia does Piatto serve?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Piatto serves venues across Australia including Sydney, Melbourne, Brisbane, Gold Coast, Perth, Adelaide, and more. Our accessible food directory is growing nationwide to help disabled diners find and order from accessible venues.",
                },
              },
            ],
          },
        ]}
      />

      {/* Scroll Progress Bar */}
      <div id="scroll-progress" className="scroll-progress" aria-hidden="true"></div>

      {/* Grain Texture Overlay */}
      <div className="grain-overlay" aria-hidden="true"></div>

      {/* Cursor Glow Effect */}
      {!prefersReducedMotion && (
        <div
          id="cursor-glow"
          className={`cursor-glow ${cursorGlowActive ? 'active' : ''}`}
          style={{ left: cursorPos.x + 'px', top: cursorPos.y + 'px' }}
          aria-hidden="true"
        ></div>
      )}

      {/* Skip Link */}
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* Screen Reader Announcements */}
      <div aria-live="assertive" aria-atomic="true" className="visually-hidden" role="status">
        {a11yAnnouncement}
      </div>

      {/* Accessibility Toolbar */}
      <div className="a11y-toolbar" role="group" aria-label="Accessibility tools">
        <button
          className={`a11y-btn ${backToTopVisible ? 'a11y-btn-visible' : 'a11y-btn-hidden'}`}
          onClick={scrollToTop}
          aria-label="Back to top"
          title="Back to top"
        >
          <span className="a11y-btn-tooltip">Back to Top</span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="18 15 12 9 6 15"/>
          </svg>
        </button>
        <button
          className="a11y-btn"
          onClick={toggleTextSize}
          aria-label={`Text size: ${textSizes[textSizeIndex]}. Click to change.`}
          title="Increase text size"
        >
          <span className="a11y-btn-tooltip">Text Size</span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="4 7 4 4 20 4 20 7"/>
            <line x1="9" y1="20" x2="15" y2="20"/>
            <line x1="12" y1="4" x2="12" y2="20"/>
          </svg>
        </button>
        <button
          className="a11y-btn"
          onClick={toggleReadAloud}
          aria-label={isReading ? 'Stop reading' : 'Read page aloud'}
          aria-pressed={isReading}
          title="Read page aloud"
        >
          <span className="a11y-btn-tooltip">Read Aloud</span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
          </svg>
        </button>
      </div>

      {/* Header */}
      <header className={`header ${headerScrolled ? 'scrolled' : ''}`} id="header" role="banner">
        <div className="header-inner">
          <a href="/" className="logo" aria-label="Piatto - Home">
            <img src={logo} alt="Piatto" className="logo-image" />
          </a>

          <nav className="nav-center" role="navigation" aria-label="Main navigation">
            <div className="nav-center-links">
              <a href="https://piatto.com.au/SignUp" className="nav-link">Venue Sign-Up</a>
              <a href="https://piatto.com.au/SignIn" className="nav-link">Venue Log-In</a>
              <a href="#contact" className="nav-link" onClick={handleContactScroll}>Contact</a>
            </div>
            <div className="nav-center-btn">
              <a href="https://piatto.com.au/SignUp" className="btn btn-primary btn-small">
                Venue Sign-Up <span className="free-badge">Free</span>
              </a>
            </div>
          </nav>

          <button
            className="mobile-menu-btn"
            ref={menuButtonRef}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-nav"
            aria-label="Toggle menu"
            onClick={toggleMobileMenu}
          >
            <span></span><span></span><span></span>
          </button>
        </div>

        <nav id="mobile-nav" className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`} aria-label="Mobile navigation" aria-hidden={!mobileMenuOpen}>
          <a href="https://piatto.com.au/SignUp" onClick={() => { setMobileMenuOpen(false); menuButtonRef.current?.focus(); }}>Venue Sign-Up</a>
          <a href="https://piatto.com.au/SignIn" onClick={() => { setMobileMenuOpen(false); menuButtonRef.current?.focus(); }}>Venue Log-In</a>
          <a href="#contact" onClick={(e) => { handleContactScroll(e); menuButtonRef.current?.focus(); }}>Contact</a>
        </nav>
      </header>

      <main id="main-content" ref={mainContentRef}>
        {/* Hero Section */}
        <section className="hero" id="hero" ref={heroRef} aria-labelledby="hero-title">
          <div className="hero-gradient" aria-hidden="true"></div>
          <div className="hero-particles" id="hero-particles" aria-hidden="true"></div>

          <div className="hero-inner">
            <header className="hero-header reveal">
              <h1 id="hero-title" className="hero-title">World's Accessible Food Directory</h1>
              <p className="hero-subtitle">QR codes are not accessible!</p>
            </header>

            <div className="hero-grid">
              <div className="hero-content reveal-left">
                <div className="hero-benefits">
                  <h2>We make your menu accessible!</h2>
                  <p className="menu-text">Enabling even the blind to read your menu.</p>
                  <p><strong>Join Piatto to:</strong></p>
                  <ul>
                    <li>Increase your market</li>
                    <li>Serve all customers</li>
                    <li>Tap into 20% of the population +
                      <ul className="sub-list">
                        <li>elderly</li>
                        <li>mothers with prams</li>
                        <li>temporarily injured</li>
                      </ul>
                    </li>
                    <li>Support accessibility and inclusion for everyone!</li>
                  </ul>
                </div>
              </div>

              <div className="hero-video-wrapper reveal-right">
                <div className="hero-video">
                  <iframe
                    src="https://www.youtube.com/embed/fj-GOVZtgKo?si=xZGz10ZwIhBVXA-F"
                    title="Yeah, we drink coffee too - Piatto introduction"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
                <div className="hero-cta">
                  <div className="magnetic-wrap">
                    <a href="https://piatto.com.au/SignUp" className="btn btn-primary btn-large magnetic-btn">
                      Venue Sign-Up <span className="free-badge">Free</span>
                    </a>
                  </div>
                  <p className="hero-cta-note">Become more accessible here</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Banner */}
        <section className="solution-banner reveal" aria-labelledby="solution-title">
          <h2 id="solution-title" className="solution-text">
            When accessibility is not an option,<br /><strong>Piatto is the solution!</strong>
          </h2>
        </section>

        {/* How It Works */}
        <section className="how-it-works" aria-labelledby="how-it-works-title">
          <div className="how-it-works-inner">
            <header className="how-it-works-header reveal">
              <h2 id="how-it-works-title" className="how-it-works-title">How It Works</h2>
              <p className="how-it-works-subtitle">Simple for venues. Life-changing for customers.</p>
            </header>
            <div className="how-it-works-grid">
              <a href="https://piatto.com.au/SignUp" className="tilt-card reveal-left card-link" data-tilt aria-label="How it works for venues — sign up free">
                <h3>For Venues <span className="free-badge">Free to Join</span></h3>
                <ol className="how-it-works-steps">
                  <li><span><strong>Sign up</strong> in just 2 minutes</span></li>
                  <li><span><strong>Add your menu</strong> and venue details</span></li>
                  <li><span><strong>Receive orders</strong> from customers who couldn't reach you before</span></li>
                </ol>
              </a>
              <a href="https://piatto.com.au/redirect.html" className="tilt-card reveal-right card-link" data-tilt aria-label="How it works for customers — download the free app">
                <h3>For Customers <span className="free-badge">Free App</span></h3>
                <ol className="how-it-works-steps">
                  <li><span><strong>Download</strong> the free Piatto app</span></li>
                  <li><span><strong>Browse ANY venue's menu</strong> and check accessibility details</span></li>
                  <li><span><strong>Order ahead</strong> — no entering, no talking, no barriers</span></li>
                </ol>
              </a>
            </div>
          </div>
        </section>

        {/* Venues Section */}
        <section className="section section-light" aria-labelledby="venues-title">
          <div className="section-inner">
            <header className="section-header reveal">
              <h2 id="venues-title" className="section-title">
                Piatto is enabling <span className="gradient-text">all venues</span> to serve <span className="gradient-text">all customers!</span>
              </h2>
              <p className="section-description">
                Allow vision impaired, hearing impaired, as well as customers with cognitive impairment to VIEW your menu, place an ORDER and ENJOY your hospitality!
              </p>
            </header>
            <div className="venues-grid">
              <a href="https://piatto.com.au/SignUp" className="venue-card reveal card-link" data-tilt aria-label="Serve Everyone — sign up to serve disabled customers even if your venue is not accessible">
                <div className="venue-card-image-placeholder">[Image: serve-everyone.png]</div>
                <div className="venue-card-content">
                  <h3 className="venue-card-title">Serve Everyone</h3>
                  <p className="venue-card-text">Serve disabled customers EVEN if your venue is not accessible! No QR necessary!</p>
                </div>
              </a>
              <a href="https://piatto.com.au/SignUp" className="venue-card reveal card-link" data-tilt aria-label="Know Before They Arrive — sign up to learn customer requirements in advance">
                <div className="venue-card-image-placeholder">[Image: know-before.png]</div>
                <div className="venue-card-content">
                  <h3 className="venue-card-title">Know Before They Arrive</h3>
                  <p className="venue-card-text">Know before they arrive, what their particular requirements are! Piatto will tell you!</p>
                </div>
              </a>
              <a href="https://piatto.com.au/SignUp" className="venue-card reveal card-link" data-tilt aria-label="Showcase Your Accessibility — sign up to highlight your venue's accessibility features">
                <div className="venue-card-image-placeholder">[Image: showcase-accessibility.png]</div>
                <div className="venue-card-content">
                  <h3 className="venue-card-title">Showcase Your Accessibility</h3>
                  <p className="venue-card-text">Have a ramp, braille/audio-narrated menus, a quiet spot for sensory needs? Those facts will be made obvious on Piatto!</p>
                </div>
              </a>
              <a href="https://piatto.com.au/SignUp" className="venue-card reveal card-link" data-tilt aria-label="Expand Your Market — sign up to reach 20% more customers with accessibility needs">
                <div className="venue-card-image-placeholder">[Image: expand-market.png]</div>
                <div className="venue-card-content">
                  <h3 className="venue-card-title">Expand Your Market</h3>
                  <p className="venue-card-text">Tap into 20% of the population + elderly, mothers with prams, and temporarily injured.</p>
                </div>
              </a>
              <a href="https://piatto.com.au/SignUp" className="venue-card reveal card-link" data-tilt aria-label="What If You Can't Accommodate — sign up to serve customers even from inaccessible venues">
                <div className="venue-card-image-placeholder">[Image: accommodate.png]</div>
                <div className="venue-card-content">
                  <h3 className="venue-card-title">What If You Can't Accommodate?</h3>
                  <p className="venue-card-text">Perhaps you wouldn't mind bringing the order outside? Even inaccessible venues can serve everyone with Piatto!</p>
                </div>
              </a>
              <a href="https://piatto.com.au/SignUp" className="venue-card reveal card-link" data-tilt aria-label="Blind Menu Access — sign up to make your menu accessible to screen readers">
                <div className="venue-card-image-placeholder">[Image: blind-access.png]</div>
                <div className="venue-card-content">
                  <h3 className="venue-card-title">Blind Menu Access</h3>
                  <p className="venue-card-text">How do the blind read your menu? Is it in braille? Piatto makes your menu accessible to screen readers, most online menus aren't!</p>
                </div>
              </a>
            </div>
            <div className="venue-cta reveal">
              <p className="venue-cta-text">
                Click the <span className="green-text" role="text">GREEN (sign-up)</span> button to make your hospitality venue, cafe, restaurant, bar, pop-up or food truck more accessible <strong>OVERNIGHT!</strong>
              </p>
              <div className="magnetic-wrap">
                <a href="https://piatto.com.au/SignUp" className="btn btn-primary btn-large magnetic-btn">
                  Venue Sign-Up <span className="free-badge">Free</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Venue Videos Section */}
        <section className="section section-cream" aria-labelledby="venue-videos-title">
          <div className="section-inner">
            <h2 id="venue-videos-title" className="visually-hidden">Why Venues Need Piatto</h2>
            <article className="feature-block">
              <div className="feature-content reveal-left">
                <h3>Why must disabled people always interrupt other guests, other diners?</h3>
                <p>With Piatto, your customers can order independently without needing to flag down staff or ask other diners for help.</p>
                <p><a href="https://piatto.com.au/SignUp" className="feature-highlight-link">Let technology handle it!</a></p>
              </div>
              <div className="feature-visual reveal-right">
                <div className="feature-video parallax-element" data-speed="0.05">
                  <iframe
                    src="https://www.youtube.com/embed/g5inASZEMYA?si=fygJrUw1wuSz1fKA"
                    title="Why must disabled people always interrupt other guests?"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
              </div>
            </article>
            <article className="feature-block reverse">
              <div className="feature-content reveal-right">
                <h3>Disabled people are NOT welcome at markets with Food Trucks!</h3>
                <p>Or are they? With Piatto, food trucks and pop-ups can serve everyone, regardless of the physical setup.</p>
                <p><a href="https://piatto.com.au/SignUp" className="feature-highlight-link">Make your food truck accessible today.</a></p>
              </div>
              <div className="feature-visual reveal-left">
                <div className="feature-video parallax-element" data-speed="0.05">
                  <iframe
                    src="https://www.youtube.com/embed/FwPTHjQ9AGE?si=RHLese7p6VnL40aD"
                    title="Disabled people are NOT welcome at markets with Food Trucks!"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
              </div>
            </article>
            <article className="feature-block">
              <div className="feature-content reveal-left">
                <h3>Who CARES if the VENUE is NOT ACCESSIBLE!</h3>
                <p>Piatto doesn't discriminate! Even if your venue has stairs, high tables, or other barriers, you can still serve disabled customers.</p>
                <p><a href="https://piatto.com.au/SignUp" className="feature-highlight-link">Piatto makes it possible.</a></p>
              </div>
              <div className="feature-visual reveal-right">
                <div className="feature-video parallax-element" data-speed="0.05">
                  <iframe
                    src="https://www.youtube.com/embed/XgHUIzbkqog?si=jz4hmfetiN1UmWuy"
                    title="Who CARES if the VENUE is NOT ACCESSIBLE!"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
              </div>
            </article>
          </div>
        </section>

        {/* Tagline Banner */}
        <section className="tagline-banner reveal" aria-labelledby="tagline-title">
          <h2 id="tagline-title" className="tagline-text">
            <span className="tagline-highlight">Enabling All</span> hospitality for <span className="tagline-highlight">All</span> people!
          </h2>
        </section>

        {/* Customer Features Section */}
        <section className="section section-white" aria-labelledby="customers-title">
          <div className="section-inner">
            <header className="section-header reveal">
              <h2 id="customers-title" className="section-title">Are you a person, not just another number?</h2>
              <p className="section-description">
                How great are all these mobile/online do-it-yourself ordering systems? Scan this QR, open this web app, give your phone number, what code did we just send you? How front-of-mind do you think 'ACCESSIBILITY' was during the design of these apps?
              </p>
            </header>
            <article className="feature-block">
              <div className="feature-content reveal-left">
                <h3>Just because you can't talk or hear, doesn't mean you can't smile and drink coffee!</h3>
                <p>Order via Piatto, no talking required! <span className="feature-highlight">Let technology submit your order!</span></p>
                <p>Not everyone can communicate their desires to the cashier. Order via Piatto, no talking required! <span className="feature-highlight">Let technology handle it!</span></p>
              </div>
              <div className="feature-visual reveal-right">
                <img src={cafeOrderingImg} alt="Person ordering coffee at a cafe counter" className="feature-image parallax-element" data-speed="0.03" />
              </div>
            </article>
            <article className="feature-block reverse">
              <div className="feature-content reveal-right">
                <h3>Save Your Accessibility Requirements</h3>
                <p>Piatto allows the user to save their accessibility requirement* in their User Profile, along with their car's rego #* and the venue will be notified along with your order!</p>
                <p className="feature-highlight">If you're in a wheelchair (like me), they may even be so kind to bring out your coffee!</p>
              </div>
              <div className="feature-visual reveal-left">
                <img src={parkingAccessibilityImg} alt="A birds eye view of three parking spaces with a sports car in the first one on the left, and two empty spaces on the right. Both of the two empty spaces feature the universal disabled symbol on the ground" className="feature-image parallax-element" data-speed="0.03" />
              </div>
            </article>
            <article className="feature-block">
              <div className="feature-content reveal-left">
                <h3>Check First on Piatto Before You Make the Effort!</h3>
                <p>Is the venue accessible, does it have an elevator or a stair lift, how do you get someone's attention to operate the stair lift?</p>
                <p>How accessible is the venue, how close are the disabled parking bays and how's the pathway from them to the venue?</p>
                <p>Is the journey to the venue accessible, how's the footpath, any steep inclinations, is there tactile paving?</p>
                <p className="feature-highlight">Piatto allows you to view the menu, submit your order and pay for your order without even entering!</p>
              </div>
              <div className="feature-visual reveal-right">
                <img src={checkFirstImg} alt="A top-down view of a 3D rendering of a ramp and a flat surface. The flat surface features tactile paving, with a girl who is wearing sunglasses and holding a cane walking straight, following the tactile paving. The ramp features a young boy in a wheelchair sitting at the bottom of the ramp" className="feature-image parallax-element" data-speed="0.03" />
              </div>
            </article>
            <article className="feature-block reverse">
              <div className="feature-content reveal-right">
                <h3>The Venue Gets a Heads Up!</h3>
                <p>The venue will get a heads up that someone with your accessibility requirement is coming* and can keep an eye out!</p>
                <p className="feature-highlight">Maybe they'll assist or just bring out your coffee! Don't shout from 20m away.</p>
              </div>
              <div className="feature-visual reveal-left">
                <img src={outdoorServiceImg} alt="An empty wheelchair sitting at the foot of a 5 stair stair-case, with a stair-lift to the left of the stationary wheelchair, up against the wall waiting to be deployed. The stair case also features two railings. One which the stair-lift supposably uses as a track and another one in the middle of the 5 stair stair-case" className="feature-image parallax-element" data-speed="0.03" />
              </div>
            </article>
            <article className="feature-block">
              <div className="feature-content reveal-left">
                <h3>Is the venue accessible or is it up a three-flight staircase, full of only high tables with bar stools?</h3>
                <p>Find out before you bother, check the venue's accessibility rating, leave your own review or just order via Piatto to give them a heads up of your particular accessibility requirements before you arrive*.</p>
                <p>How accessible is the venue, check first on Piatto before you take your client there! Let's make the next day out, the easiest, most enjoyable, for ALL!</p>
              </div>
              <div className="feature-visual reveal-right">
                <img src={cafeAccessibleImg} alt="A table and chair within a venue which appears to be accessible. A male waiter stands in front of the table and gestures a young boy using a wheelchair followed by a male, able-boded companion, to take their seat at this table." className="feature-image parallax-element" data-speed="0.03" />
              </div>
            </article>
            <article className="feature-block reverse">
              <div className="feature-content reveal-right">
                <h3>Browse, Order & Pay Before Arriving</h3>
                <p>Piatto allows users to browse nearby cafés, restaurants, bars, pop-ups, food vans/trucks to peruse their menus and place an order before even arriving!</p>
                <p className="feature-highlight">So you can spend more time enjoying!</p>
                <p>Is the cafe accessible? How's the footpath to get there? What's the disabled parking situation? <span className="feature-highlight">Let's check on Piatto!</span></p>
              </div>
              <div className="feature-visual reveal-left">
                <img src={browseOrderImg} alt="Person relaxing in a single seater chair while browsing a menu on their phone and making selections before leaving the comfort of their chair." className="feature-image parallax-element" data-speed="0.03" />
              </div>
            </article>
            <div className="highlight-box reveal">
              <p><strong>Piatto is ALL about accessibility, inclusion and ease of use!</strong> Help us make Piatto better!</p>
            </div>
          </div>
        </section>

        {/* Carers Section */}
        <section className="section carers-section" aria-labelledby="carers-title">
          <div className="section-inner">
            <div className="carers-grid">
              <div className="carers-content reveal-left">
                <h2 id="carers-title">Planning an outing? Piatto has you covered.</h2>
                <p>Personal/Executive Assistants, support workers, carers, and family members spend hours researching venues. Piatto makes it simple.</p>
                <ul className="carers-list">
                  <li>Check venue accessibility details before you leave</li>
                  <li>Your client's requirements are automatically shared when ordering</li>
                  <li>No more awkward phone calls or surprises on arrival</li>
                  <li>Order ahead so you can focus on enjoying the outing</li>
                  <li>Leave accessibility reviews to help other carers</li>
                  <li>Plan the outing but direct the bill to someone else</li>
                </ul>
                <div className="carers-quote">
                  <p>"I used to spend hours calling venues and hoping for the best. Now I check Piatto and we're out the door."</p>
                </div>
              </div>
              <div className="carers-visual feature-visual reveal-right">
                <img src={carersOutingImg} alt="Accessibility requirements and planning for carers" className="feature-image parallax-element" data-speed="0.03" />
              </div>
            </div>
          </div>
        </section>

        {/* Accessibility Cards */}
        <section className="section section-light" aria-labelledby="accessibility-title">
          <div className="section-inner">
            <header className="section-header reveal">
              <h2 id="accessibility-title" className="section-title">
                Accessibility for <span className="gradient-text">Everyone</span>
              </h2>
            </header>
            <div className="accessibility-grid">
              <a href="https://piatto.com.au/SignUp" className="accessibility-card reveal card-link" data-tilt aria-label="Vision Impaired — learn how Piatto makes menus accessible to screen readers">
                <div className="accessibility-card-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </div>
                <h3>Vision Impaired</h3>
                <p>How do the blind read your menu? How does a blind person know if there is table service or they must pay at the counter? Piatto makes menus accessible to screen readers.</p>
              </a>
              <a href="https://piatto.com.au/SignUp" className="accessibility-card reveal card-link" data-tilt aria-label="Hearing Impaired — order via Piatto with no talking required">
                <div className="accessibility-card-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="23"/>
                    <line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                </div>
                <h3>Hearing Impaired</h3>
                <p>Just because you can't talk or hear, doesn't mean you can't smile and drink coffee! Order via Piatto, no talking required!</p>
              </a>
              <a href="https://piatto.com.au/SignUp" className="accessibility-card reveal card-link" data-tilt aria-label="Cognitive Impairment — simple, clear interface with dyslexia-friendly fonts">
                <div className="accessibility-card-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4"/>
                    <path d="M12 8h.01"/>
                  </svg>
                </div>
                <h3>Cognitive Impairment</h3>
                <p>Simple, clear interface with dyslexia-friendly fonts. Browse menus at your own pace without pressure. Order when you're ready.</p>
              </a>
              <a href="https://piatto.com.au/SignUp" className="accessibility-card reveal card-link" data-tilt aria-label="Mobility Impaired — order without entering the venue">
                <div className="accessibility-card-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <circle cx="12" cy="12" r="4"/>
                    <line x1="21.17" y1="8" x2="12" y2="8"/>
                    <line x1="3.95" y1="6.06" x2="8.54" y2="14"/>
                    <line x1="10.88" y1="21.94" x2="15.46" y2="14"/>
                  </svg>
                </div>
                <h3>Mobility Impaired</h3>
                <p>Is the venue accessible? Is there a ramp? How steep? Order without even entering! The venue gets a heads up you're coming.</p>
              </a>
              <a href="https://piatto.com.au/SignUp" className="accessibility-card reveal card-link" data-tilt aria-label="Temporarily Challenged — browse menus before you arrive to avoid wasted trips">
                <div className="accessibility-card-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <h3>Temporarily Challenged</h3>
                <p>Elderly, temporarily injured, or pushing a pram? Browse menus before you arrive — no wasted trips across grassy hills or through crowded food markets only to find stairs or sold-out items.</p>
              </a>
              <a href="https://piatto.com.au/SignUp" className="accessibility-card reveal card-link" data-tilt aria-label="Live Availability — check if the venue is open before making the effort to get there">
                <div className="accessibility-card-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <h3>Live Availability</h3>
                <p>Is the venue open or did they close early? Is the coffee machine still on? Are they still serving breakfast? Check live availability before making the effort to get there.</p>
              </a>
            </div>
          </div>
        </section>

        {/* User CTA Section */}
        <section className="user-cta-section" aria-labelledby="user-cta-title">
          <div className="user-cta-inner">
            <h2 id="user-cta-title" className="user-cta-title reveal">Join the Piatto community!</h2>
            <p className="user-cta-text reveal">Enable us to make hospitality more accessible, easy and efficient for you!</p>
            <div className="app-badges reveal">
              <a href="https://piatto.com.au/redirect.html" className="app-badge-link" aria-label="Get it on Google Play">
                <img src={googlePlayButton} alt="Get it on Google Play" className="app-badge-img" />
              </a>
              <a href="https://piatto.com.au/redirect.html" className="app-badge-link" aria-label="Download on the App Store">
                <img src={appStoreButton} alt="Download on the App Store" className="app-badge-img" />
              </a>
            </div>
            <p className="app-note reveal">* = Optional</p>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="contact-section" aria-labelledby="contact-title">
          <div className="contact-inner">
            <header className="contact-header reveal">
              <h2 id="contact-title" className="contact-title">Get in Touch</h2>
              <p className="contact-subtitle">Interested in Piatto? Have questions? We'd love to hear from you!</p>
            </header>
            <form className="contact-form reveal-scale" onSubmit={handleContactSubmit} aria-label="Contact form">
              <div className="form-row two-col">
                <div className="form-group">
                  <label htmlFor="first-name" className="form-label">First Name <span className="required" aria-hidden="true">*</span></label>
                  <input type="text" id="first-name" name="first-name" className="form-input" required aria-required="true" autoComplete="given-name" placeholder="Your first name" />
                </div>
                <div className="form-group">
                  <label htmlFor="last-name" className="form-label">Last Name <span className="required" aria-hidden="true">*</span></label>
                  <input type="text" id="last-name" name="last-name" className="form-input" required aria-required="true" autoComplete="family-name" placeholder="Your last name" />
                </div>
              </div>
              <div className="form-row two-col">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email <span className="required" aria-hidden="true">*</span></label>
                  <input type="email" id="email" name="email" className="form-input" required aria-required="true" autoComplete="email" placeholder="you@example.com" />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="form-input"
                    autoComplete="tel"
                    placeholder="0400 000 000"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    aria-describedby="phone-hint"
                    onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                  />
                  <span id="phone-hint" className="phone-hint">Numbers only (e.g. 0400000000)</span>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="interest" className="form-label">I am interested as a... <span className="required" aria-hidden="true">*</span></label>
                  <select id="interest" name="interest" className="form-select" required aria-required="true">
                    <option value="">Please select...</option>
                    <option value="venue">Venue Owner / Manager</option>
                    <option value="user">App User / Customer</option>
                    <option value="carer">Carer / Support Worker</option>
                    <option value="assistant">Personal / Executive Assistant</option>
                    <option value="partner">Potential Partner</option>
                    <option value="media">Media / Press</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="venue-name" className="form-label">Venue Name (if applicable)</label>
                  <input type="text" id="venue-name" name="venue-name" className="form-input" placeholder="Your venue name" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message <span className="required" aria-hidden="true">*</span></label>
                  <textarea id="message" name="message" className="form-textarea" required aria-required="true" placeholder="Tell us how we can help you..."></textarea>
                </div>
              </div>
              <div className="magnetic-wrap" style={{ width: '100%' }}>
                <button
                  type="submit"
                  className="btn btn-primary btn-large form-submit magnetic-btn"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                  aria-label={isSubmitting ? 'Sending your message' : 'Send message'}
                  style={{ opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
              {formFeedback.message && (
                <div
                  role="alert"
                  aria-live="assertive"
                  aria-atomic="true"
                  className={`form-feedback form-feedback-${formFeedback.type}`}
                >
                  {formFeedback.message}
                </div>
              )}
              <p className="form-note">We'll get back to you within 1-2 business days.</p>
            </form>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="footer" role="contentinfo">
        <div className="footer-inner">
          <div className="footer-top">
            <div className="footer-brand">
              <img src={logo} alt="Piatto - World's Accessible Food Directory" className="footer-logo" />
              <p>World's Accessible Food Directory. Enabling all hospitality for all people.</p>
            </div>
            <nav aria-label="Product links">
              <h3 className="footer-heading">Product</h3>
              <ul className="footer-links">
                <li><a href="#user-cta-title">Download App</a></li>
              </ul>
            </nav>
            <nav aria-label="Venue links">
              <h3 className="footer-heading">Venues</h3>
              <ul className="footer-links">
                <li><a href="https://piatto.com.au/SignUp">Venue Sign-Up</a></li>
                <li><a href="https://piatto.com.au/SignIn">Venue Log-In</a></li>
              </ul>
            </nav>
            <nav aria-label="Resources">
              <h3 className="footer-heading">Resources</h3>
              <ul className="footer-links">
                <li><a href="https://piatto.substack.com/" target="_blank" rel="noopener noreferrer">Blog</a></li>
                <li><a href="/accessibility-statement">Accessibility Statement</a></li>
              </ul>
            </nav>
          </div>
          <div className="footer-social" aria-label="Social media links">
            <a href="http://youtube.com/@piattoapp" aria-label="YouTube" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/>
                <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/>
              </svg>
            </a>
            <a href="https://www.instagram.com/piattoit" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
              </svg>
            </a>
            <a href="https://www.facebook.com/piattoAUS" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
            </a>
            <a href="https://twitter.com/@PiattoNow" aria-label="X (Twitter)" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a href="https://piatto.substack.com/" aria-label="Substack" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"/>
              </svg>
            </a>
            <a href="https://www.linkedin.com/company/piattoaus/" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                <rect x="2" y="9" width="4" height="12"/>
                <circle cx="4" cy="4" r="2"/>
              </svg>
            </a>
            <a href="https://discord.com/invite/6dcdt2d24x" aria-label="Discord" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.373-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
            </a>
          </div>
          <div className="footer-bottom">
            <p className="footer-copyright">&copy; 2024 Piatto. All rights reserved.</p>
            <nav className="footer-legal" aria-label="Legal links">
              <div className="footer-legal-row">
                <a href="/Piatto_Privacy_Policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy<span className="sr-only"> (opens in new tab)</span></a>
                <a href="/Piatto_Complaint_Policy.pdf" target="_blank" rel="noopener noreferrer">Complaints Policy<span className="sr-only"> (opens in new tab)</span></a>
                <a href="/Piatto_Data_Breach_Policy.pdf" target="_blank" rel="noopener noreferrer">Data Breach Policy<span className="sr-only"> (opens in new tab)</span></a>
              </div>
              <div className="footer-legal-row">
                <a href="/Piatto_Terms_and_Conditions_for_Venues.pdf" target="_blank" rel="noopener noreferrer">Terms and Conditions for Venues<span className="sr-only"> (opens in new tab)</span></a>
                <a href="/Piatto_Terms_and_Conditions_for_Users.pdf" target="_blank" rel="noopener noreferrer">Terms and Conditions for Users<span className="sr-only"> (opens in new tab)</span></a>
              </div>
            </nav>
          </div>
        </div>
      </footer>

      <style jsx>{`
        /* Import Google Fonts - Atkinson Hyperlegible (primary) & Lexend (secondary) */
        @import url('https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible+Next:ital,wght@0,400;0,700;1,400;1,700&family=Lexend:wght@300;400;500;600;700&display=swap');

        /* CSS Variables */
        :root {
          --font-size-base: 22px;
          --color-bg-dark: #0a0a0a;
          --color-bg-dark-alt: #1a1a1a;
          --color-bg-light: #f5f5f5;
          --color-bg-cream: #faf8f5;
          --color-surface: #ffffff;
          --color-text-dark: #1a1a1a;
          --color-text-light: #ffffff;
          --color-text-muted: #4a4a4a;
          --color-text-muted-light: rgba(255,255,255,0.85);
          --color-primary: #c41e3a;
          --color-primary-hover: #a01830;
          --color-primary-light: #fce8eb;
          --color-accent: #7cb342;
          --color-accent-hover: #689f38;
          --color-accent-light: #e8f5e9;
          --color-border: #e0e0e0;
          --color-border-dark: rgba(255,255,255,0.1);
          --shadow-md: 0 4px 20px rgba(0,0,0,0.1);
          --shadow-lg: 0 12px 40px rgba(0,0,0,0.2);
          --shadow-glow: 0 0 40px rgba(196, 30, 58, 0.3);
          --radius-sm: 12px;
          --radius-md: 20px;
          --radius-lg: 28px;
          --radius-full: 9999px;
          --transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          --transition-slow: 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          --font-primary: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
          --font-secondary: 'Lexend', system-ui, -apple-system, sans-serif;
          --glass-bg: rgba(255, 255, 255, 0.08);
          --glass-border: rgba(255, 255, 255, 0.12);
        }

        /* Text Size Classes */
        :global(html.text-size-large) { --font-size-base: 22px; }
        :global(html.text-size-larger) { --font-size-base: 24px; }
        :global(html.text-size-largest) { --font-size-base: 26px; }

        /* Reset & Base */
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :global(html) { scroll-behavior: smooth; font-size: var(--font-size-base); }
        @media (max-width: 768px) { :global(html) { font-size: calc(var(--font-size-base) - 1px); } }

        :global(body) {
          font-family: var(--font-primary);
          background: var(--color-bg-light);
          color: var(--color-text-dark);
          line-height: 1.75;
          letter-spacing: 0.01em;
          min-height: 100vh;
          -webkit-font-smoothing: antialiased;
          overflow-x: hidden;
        }

        /* Scroll Progress Bar */
        .scroll-progress {
          position: fixed;
          top: 0;
          left: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-accent));
          width: 0%;
          z-index: 1000;
          transition: width 0.1s linear;
        }

        /* Grain Texture Overlay */
        .grain-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 9999;
          opacity: 0.03;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
        }

        /* Cursor Glow */
        .cursor-glow {
          position: fixed;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(196, 30, 58, 0.15) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
          z-index: 1;
          transform: translate(-50%, -50%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .cursor-glow.active { opacity: 1; }

        /* Skip Link */
        .skip-link {
          position: absolute;
          top: -100%;
          left: 1rem;
          background: var(--color-primary);
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: var(--radius-sm);
          text-decoration: none;
          font-weight: 600;
          z-index: 1001;
          transition: top var(--transition);
        }
        .skip-link:focus { top: 1rem; outline: 3px solid var(--color-accent); }
        :global(*:focus-visible) { outline: 3px solid var(--color-primary); outline-offset: 3px; }

        /* Accessibility Toolbar */
        .a11y-toolbar {
          position: fixed;
          bottom: 1.5rem;
          right: 1.5rem;
          z-index: 99;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .a11y-btn {
          width: 50px;
          height: 50px;
          border-radius: var(--radius-full);
          border: 2px solid var(--color-border);
          background: var(--color-surface);
          color: var(--color-text-dark);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: var(--shadow-md);
          transition: all var(--transition);
          position: relative;
          backdrop-filter: blur(10px);
        }
        .a11y-btn:hover, .a11y-btn:focus {
          background: var(--color-primary);
          color: white;
          border-color: var(--color-primary);
          transform: scale(1.1);
          box-shadow: var(--shadow-glow);
        }
        .a11y-btn svg { width: 24px; height: 24px; }
        .a11y-btn[aria-pressed="true"] { background: var(--color-accent); color: white; border-color: var(--color-accent); }
        .a11y-btn-hidden { opacity: 0; transform: scale(0); pointer-events: none; }
        .a11y-btn-visible { opacity: 1; transform: scale(1); pointer-events: auto; }
        .a11y-btn-tooltip {
          position: absolute;
          right: 60px;
          background: var(--color-bg-dark);
          color: var(--color-text-light);
          padding: 0.5rem 0.75rem;
          border-radius: var(--radius-sm);
          font-size: 0.85rem;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity var(--transition);
        }
        .a11y-btn:hover .a11y-btn-tooltip, .a11y-btn:focus .a11y-btn-tooltip { opacity: 1; }

        /* Header */
        .header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          background: rgba(10, 10, 10, 0.7);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid var(--glass-border);
          transition: all var(--transition);
        }
        .header.scrolled {
          background: rgba(10, 10, 10, 0.9);
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
        }
        .header-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0.75rem 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .logo { display: flex; align-items: center; text-decoration: none; }
        .logo-image {
          height: 55px;
          width: auto;
          object-fit: contain;
          transition: transform var(--transition);
        }
        .logo:hover .logo-image { transform: scale(1.05); }
        .logo-placeholder {
          font-size: 1.75rem;
          font-weight: 700;
          font-style: italic;
          background: linear-gradient(135deg, var(--color-primary), #ff6b6b);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .nav-center {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 3rem;
        }
        .nav-center-links {
          display: flex;
          align-items: center;
          gap: 3rem;
        }
        .nav-link {
          color: var(--color-text-light);
          text-decoration: none;
          font-size: 0.95rem;
          font-weight: 500;
          transition: all var(--transition);
          white-space: nowrap;
          position: relative;
        }
        .nav-link::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 0;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-accent));
          transition: width var(--transition);
        }
        .nav-link:hover::after, .nav-link:focus::after { width: 100%; }
        .nav-link:hover, .nav-link:focus { color: var(--color-text-muted-light); }

        .header.scrolled .nav-center-links { display: none; }
        .header.scrolled .nav-center-btn { display: flex; }
        .nav-center-btn { display: none; }

        @media (max-width: 768px) {
          .nav-center { display: none; }
        }

        .mobile-menu-btn {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.5rem;
        }
        @media (max-width: 768px) { .mobile-menu-btn { display: flex; } }
        .mobile-menu-btn span { width: 24px; height: 2px; background: var(--color-text-light); border-radius: 2px; transition: all var(--transition); }
        .mobile-menu-btn[aria-expanded="true"] span:nth-child(1) { transform: rotate(45deg) translate(5px, 5px); }
        .mobile-menu-btn[aria-expanded="true"] span:nth-child(2) { opacity: 0; }
        .mobile-menu-btn[aria-expanded="true"] span:nth-child(3) { transform: rotate(-45deg) translate(5px, -5px); }

        .mobile-nav {
          position: fixed;
          top: 60px;
          right: -300px;
          width: 250px;
          background: rgba(10, 10, 10, 0.98);
          backdrop-filter: blur(20px);
          padding: 1.5rem 1rem;
          z-index: 99;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          box-shadow: -4px 0 20px rgba(0, 0, 0, 0.5);
          transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-left: 1px solid var(--glass-border);
        }
        .mobile-nav.open { right: 0; }
        .mobile-nav a {
          display: block;
          padding: 1rem;
          color: var(--color-text-light);
          text-decoration: none;
          font-size: 1rem;
          font-weight: 500;
          border-radius: var(--radius-sm);
          transition: all var(--transition);
          text-align: left;
        }
        .mobile-nav a:hover { background: var(--glass-bg); transform: translateX(-5px); }

        /* Buttons */
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          font-family: var(--font-primary);
          font-size: 0.95rem;
          font-weight: 600;
          text-decoration: none;
          border-radius: var(--radius-sm);
          border: none;
          cursor: pointer;
          transition: all var(--transition);
          position: relative;
          overflow: hidden;
        }
        .btn-primary {
          background: linear-gradient(135deg, var(--color-accent), #5a9216);
          color: var(--color-text-light);
          box-shadow: 0 4px 15px rgba(124, 179, 66, 0.3);
        }
        .btn-primary:hover, .btn-primary:focus {
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(124, 179, 66, 0.4);
        }
        .btn-primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s ease;
        }
        .btn-primary:hover::before { left: 100%; }
        .btn-large { padding: 1rem 2rem; font-size: 1.1rem; }
        .btn-small { padding: 0.6rem 1.25rem; font-size: 0.9rem; }

        .free-badge {
          display: inline-block;
          background: rgba(255,255,255,0.2);
          color: var(--color-text-light);
          font-size: 0.65rem;
          font-weight: 700;
          padding: 0.2rem 0.5rem;
          border-radius: var(--radius-full);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-left: 0.4rem;
        }

        .magnetic-wrap {
          display: inline-block;
          position: relative;
        }

        /* Hero Section */
        .hero {
          background: var(--color-bg-dark);
          color: var(--color-text-light);
          padding: 2rem 1.5rem 4rem;
          position: relative;
          overflow: hidden;
          min-height: 100vh;
          display: flex;
          align-items: center;
          padding-bottom: 12vh;
        }
        @media (max-width: 991px) {
          .hero {
            align-items: flex-start;
            padding-top: 3rem;
          }
        }
        @media (max-width: 768px) {
          .hero {
            padding-top: 2.5rem;
          }
        }
        .hero-gradient {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background:
            radial-gradient(ellipse 80% 50% at 20% 40%, rgba(196, 30, 58, 0.25) 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 80% 60%, rgba(124, 179, 66, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse 50% 30% at 50% 20%, rgba(196, 30, 58, 0.2) 0%, transparent 50%);
          animation: gradientShift 15s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes gradientShift {
          0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
          25% { transform: scale(1.1) rotate(1deg); opacity: 0.9; }
          50% { transform: scale(1.05) rotate(-1deg); opacity: 1; }
          75% { transform: scale(1.15) rotate(0.5deg); opacity: 0.85; }
        }

        .hero-particles {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          pointer-events: none;
        }
        :global(.hero-particle) {
          position: absolute;
          width: 4px;
          height: 4px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          animation: float 20s infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(100vh) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-100vh) rotate(720deg); opacity: 0; }
        }

        .hero-inner {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
          width: 100%;
        }
        .hero-header { text-align: center; margin-bottom: 3rem; }

        .hero-title {
          font-size: clamp(2.5rem, 6vw, 4rem);
          font-weight: 700;
          line-height: 1.1;
          margin-bottom: 0.75rem;
          background: linear-gradient(135deg, #ffffff 0%, #ffffff 40%, var(--color-primary) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .hero-subtitle {
          font-size: clamp(1.1rem, 2.5vw, 1.5rem);
          color: var(--color-text-muted-light);
          font-weight: 400;
        }
        .gradient-text {
          background: linear-gradient(135deg, var(--color-primary), #ff6b6b, var(--color-accent));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          background-size: 200% auto;
          animation: gradientText 4s ease infinite;
        }
        @keyframes gradientText {
          0%, 100% { background-position: 0% center; }
          50% { background-position: 100% center; }
        }

        .hero-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
          align-items: center;
        }
        @media (min-width: 992px) {
          .hero-grid {
            grid-template-columns: 1fr 1.3fr;
            gap: 1rem;
            align-items: start;
          }
        }
        .hero-benefits {
          padding-top: 0;
        }
        @media (max-width: 991px) {
          .hero-content {
            display: flex;
            justify-content: center;
          }
          .hero-benefits {
            max-width: 500px;
          }
        }
        @media (min-width: 992px) {
          .hero-benefits {
            padding-top: 0;
          }
        }
        .hero-benefits h2 { font-size: 1.35rem; margin-bottom: 0.75rem; }
        .hero-benefits .menu-text { margin-bottom: 1.25rem; opacity: 0.9; font-size: 1.1rem; }
        .hero-benefits ul { list-style: none; margin-bottom: 0.75rem; }
        .hero-benefits li {
          padding: 0.5rem 0;
          padding-left: 1.75rem;
          position: relative;
          font-size: 1.15rem;
        }
        .hero-benefits li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.8rem;
          width: 8px;
          height: 8px;
          background: linear-gradient(135deg, var(--color-accent), var(--color-primary));
          border-radius: 50%;
        }
        .hero-benefits .sub-list {
          padding-left: 1.75rem;
          margin-top: 0.5rem;
          list-style: none;
        }
        .hero-benefits .sub-list li {
          padding: 0.35rem 0;
          padding-left: 1.25rem;
          position: relative;
          font-size: 1.05rem;
        }
        .hero-benefits .sub-list li::before {
          width: 6px;
          height: 6px;
          left: 0;
          top: 0.65rem;
          transform: none;
        }

        .hero-video-wrapper { display: flex; flex-direction: column; align-items: center; }
        @media (min-width: 992px) {
          .hero-video-wrapper {
            margin-left: -2rem;
          }
        }

        .hero-video {
          width: 100%;
          border-radius: var(--radius-lg);
          overflow: hidden;
          box-shadow: var(--shadow-lg), 0 0 60px rgba(196, 30, 58, 0.2);
          background: var(--glass-bg);
          border: 1px solid var(--glass-border);
          backdrop-filter: blur(10px);
        }
        .hero-video iframe { width: 100%; aspect-ratio: 16/9; border: none; display: block; }
        .hero-cta { text-align: center; margin-top: 2rem; }
        .hero-cta-note { margin-top: 0.75rem; font-size: 0.95rem; opacity: 0.8; }

        /* Scroll Reveal Animations */
        :global(.reveal) {
          opacity: 0;
          transform: translateY(40px);
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        :global(.reveal.revealed) {
          opacity: 1;
          transform: translateY(0);
        }
        :global(.reveal-left) {
          opacity: 0;
          transform: translateX(-60px);
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        :global(.reveal-left.revealed) {
          opacity: 1;
          transform: translateX(0);
        }
        :global(.reveal-right) {
          opacity: 0;
          transform: translateX(60px);
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        :global(.reveal-right.revealed) {
          opacity: 1;
          transform: translateX(0);
        }
        :global(.reveal-scale) {
          opacity: 0;
          transform: scale(0.9);
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        :global(.reveal-scale.revealed) {
          opacity: 1;
          transform: scale(1);
        }

        /* Solution Banner */
        .solution-banner {
          background: linear-gradient(135deg, var(--color-primary), #8b1528);
          color: var(--color-text-light);
          text-align: center;
          padding: 3rem 1.5rem;
          position: relative;
          overflow: hidden;
        }
        .solution-banner::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          opacity: 0.5;
        }
        .solution-text {
          font-size: clamp(1.25rem, 3vw, 1.75rem);
          font-weight: 600;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.5;
          position: relative;
          z-index: 1;
        }

        /* How It Works */
        .how-it-works {
          background: var(--color-surface);
          padding: 6rem 1.5rem;
          position: relative;
        }
        .how-it-works-inner { max-width: 1100px; margin: 0 auto; }
        .how-it-works-header { text-align: center; margin-bottom: 4rem; }
        .how-it-works-title {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 700;
          margin-bottom: 0.75rem;
        }
        .how-it-works-subtitle { font-size: 1.15rem; color: var(--color-text-muted); }
        .how-it-works-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
        @media (min-width: 768px) { .how-it-works-grid { grid-template-columns: 1fr 1fr; gap: 3rem; } }

        .tilt-card {
          background: var(--color-bg-light);
          border-radius: var(--radius-lg);
          padding: 2.5rem;
          border: 1px solid var(--color-border);
          transition: all var(--transition);
          transform-style: preserve-3d;
          perspective: 1000px;
        }
        .tilt-card:hover {
          box-shadow: var(--shadow-lg);
        }
        .tilt-card h3 {
          font-size: 1.35rem;
          font-weight: 700;
          margin-bottom: 1.75rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        .tilt-card h3 .free-badge {
          background: var(--color-accent-light);
          color: var(--color-accent-hover);
          font-size: 0.65rem;
        }
        .how-it-works-steps { list-style: none; counter-reset: step; }
        .how-it-works-steps li {
          counter-increment: step;
          display: flex;
          align-items: flex-start;
          gap: 1.25rem;
          margin-bottom: 1.5rem;
          font-size: 1.1rem;
        }
        .how-it-works-steps li::before {
          content: counter(step);
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, var(--color-primary), #ff6b6b);
          color: white;
          border-radius: var(--radius-full);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.95rem;
          box-shadow: 0 4px 15px rgba(196, 30, 58, 0.3);
        }
        .how-it-works-steps li strong { color: var(--color-primary); }

        /* Sections */
        .section { padding: 6rem 1.5rem; }
        .section-inner { max-width: 1100px; margin: 0 auto; }
        .section-light { background: var(--color-bg-light); }
        .section-white { background: var(--color-surface); }
        .section-cream { background: var(--color-bg-cream); }
        .section-header { text-align: center; max-width: 850px; margin: 0 auto 4rem; }
        .section-title {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 1.25rem;
        }
        .section-description {
          font-size: 1.2rem;
          color: var(--color-text-muted);
          line-height: 1.8;
        }

        /* Venue Cards */
        .venues-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2rem;
          margin-top: 3rem;
        }
        .venue-card {
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          overflow: hidden;
          box-shadow: var(--shadow-md);
          transition: all var(--transition);
          border: 1px solid var(--color-border);
          transform-style: preserve-3d;
          perspective: 1000px;
        }
        .venue-card:hover {
          transform: translateY(-8px);
          box-shadow: var(--shadow-lg);
        }
        .venue-card-image-placeholder {
          width: 100%;
          height: 180px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.85rem;
          color: var(--color-text-muted);
          text-align: center;
          padding: 1rem;
          position: relative;
          overflow: hidden;
        }
        .venue-card-image-placeholder::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: inherit;
          filter: blur(0);
          transition: filter var(--transition);
        }
        .venue-card:hover .venue-card-image-placeholder::before { filter: blur(2px); }
        .venue-card:nth-child(1) .venue-card-image-placeholder { background: linear-gradient(135deg, #fce8eb, #f8d7da); }
        .venue-card:nth-child(2) .venue-card-image-placeholder { background: linear-gradient(135deg, #e8f5e9, #c8e6c9); }
        .venue-card:nth-child(3) .venue-card-image-placeholder { background: linear-gradient(135deg, #e3f2fd, #bbdefb); }
        .venue-card:nth-child(4) .venue-card-image-placeholder { background: linear-gradient(135deg, #fff3e0, #ffe0b2); }
        .venue-card:nth-child(5) .venue-card-image-placeholder { background: linear-gradient(135deg, #f3e5f5, #e1bee7); }
        .venue-card:nth-child(6) .venue-card-image-placeholder { background: linear-gradient(135deg, #e0f7fa, #b2ebf2); }
        .venue-card-content { padding: 1.75rem; }
        .venue-card-title {
          font-size: 1.2rem;
          font-weight: 700;
          margin-bottom: 0.6rem;
          color: var(--color-text-dark);
        }
        .venue-card-text { color: var(--color-text-muted); font-size: 1.02rem; line-height: 1.75; }

        .venue-cta {
          text-align: center;
          margin-top: 4rem;
          padding-top: 2.5rem;
          border-top: 1px solid var(--color-border);
        }
        .venue-cta-text {
          font-size: 1.35rem;
          margin-bottom: 1.75rem;
          line-height: 1.6;
          color: var(--color-text-dark);
        }
        .green-text {
          background: linear-gradient(135deg, var(--color-accent), #5a9216);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 700;
        }

        /* Feature Blocks */
        .feature-block {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: center;
          margin-bottom: 5rem;
        }
        @media (min-width: 768px) {
          .feature-block { grid-template-columns: 1fr 1fr; gap: 4rem; }
          .feature-block.reverse .feature-content { order: 2; }
          .feature-block.reverse .feature-visual { order: 1; }
        }
        .feature-content h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1.25rem;
          color: var(--color-text-dark);
          line-height: 1.35;
        }
        .feature-content p {
          font-size: 1.1rem;
          color: var(--color-text-muted);
          margin-bottom: 1.25rem;
          line-height: 1.85;
        }
        .feature-highlight {
          background: linear-gradient(135deg, var(--color-primary), #ff6b6b);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 600;
        }
        .feature-highlight-link {
          background: linear-gradient(135deg, var(--color-primary), #ff6b6b);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 600;
          text-decoration: none;
          border-bottom: 2px solid transparent;
          transition: border-color 0.3s ease;
        }
        .feature-highlight-link:hover {
          border-bottom-color: var(--color-primary);
        }
        .card-link {
          text-decoration: none;
          color: inherit;
          display: block;
          cursor: pointer;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .card-link:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }
        a.accessibility-card { display: flex; flex-direction: column; }
        a.venue-card { display: flex; flex-direction: column; }
        a.tilt-card { display: block; }
        .feature-visual { display: flex; justify-content: center; }
        .image-placeholder {
          width: 100%;
          max-width: 450px;
          aspect-ratio: 4/3;
          background: linear-gradient(135deg, var(--color-primary-light), #f0f0f0);
          border-radius: var(--radius-lg);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--color-text-muted);
          font-size: 0.9rem;
          text-align: center;
          padding: 1rem;
          transition: all var(--transition);
          box-shadow: var(--shadow-md);
        }
        .feature-image {
          width: 100%;
          max-width: 450px;
          height: auto;
          border-radius: var(--radius-lg);
          object-fit: cover;
          transition: all var(--transition);
          box-shadow: var(--shadow-md);
        }
        .feature-image:hover {
          box-shadow: var(--shadow-lg);
          filter: brightness(1.05);
        }
        .feature-video {
          width: 100%;
          max-width: 560px;
          border-radius: var(--radius-lg);
          overflow: hidden;
          box-shadow: var(--shadow-lg);
          transition: all var(--transition);
        }
        .feature-video:hover {
          box-shadow: 0 20px 50px rgba(0,0,0,0.2);
          filter: brightness(1.05);
        }
        .feature-video iframe { width: 100%; aspect-ratio: 16/9; border: none; display: block; }

        /* Carers Section */
        .carers-section {
          background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 50%, #a5d6a7 100%);
          position: relative;
          overflow: hidden;
        }
        .carers-section::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 60%;
          height: 200%;
          background: radial-gradient(ellipse, rgba(255,255,255,0.4) 0%, transparent 70%);
          pointer-events: none;
        }
        .carers-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: center;
          position: relative;
          z-index: 1;
        }
        @media (min-width: 768px) { .carers-grid { grid-template-columns: 1.2fr 1fr; gap: 4rem; } }
        .carers-content h2 {
          font-size: clamp(1.75rem, 3.5vw, 2.25rem);
          font-weight: 700;
          margin-bottom: 1.25rem;
          color: var(--color-text-dark);
        }
        .carers-content > p { font-size: 1.15rem; color: var(--color-text-muted); margin-bottom: 1.75rem; }
        .carers-list { list-style: none; margin-bottom: 2rem; }
        .carers-list li {
          padding: 0.6rem 0;
          padding-left: 2.25rem;
          position: relative;
          font-size: 1.1rem;
          color: var(--color-text-dark);
        }
        .carers-list li::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: var(--color-accent-hover);
          font-weight: bold;
          font-size: 1.2rem;
        }
        .carers-quote {
          background: var(--color-surface);
          border-radius: var(--radius-md);
          padding: 1.75rem;
          border-left: 4px solid var(--color-accent);
          box-shadow: var(--shadow-md);
        }
        .carers-quote p { font-style: italic; color: var(--color-text-muted); margin: 0; font-size: 1.05rem; }
        .carers-visual .image-placeholder { background: linear-gradient(135deg, #a5d6a7, #81c784); }

        /* Accessibility Cards */
        .accessibility-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }
        .accessibility-card {
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          padding: 2.25rem;
          border: 1px solid var(--color-border);
          transition: all var(--transition);
          transform-style: preserve-3d;
          perspective: 1000px;
        }
        .accessibility-card:hover {
          transform: translateY(-8px);
          box-shadow: var(--shadow-lg);
          border-color: var(--color-primary);
        }
        .accessibility-card-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, var(--color-primary), #ff6b6b);
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
          color: white;
          box-shadow: 0 8px 20px rgba(196, 30, 58, 0.3);
        }
        .accessibility-card-icon svg { width: 30px; height: 30px; }
        .accessibility-card h3 { font-size: 1.2rem; font-weight: 600; margin-bottom: 0.85rem; color: var(--color-text-dark); }
        .accessibility-card p { color: var(--color-text-muted); font-size: 1.02rem; line-height: 1.8; }

        /* User CTA Section */
        .user-cta-section {
          background: linear-gradient(135deg, var(--color-bg-dark) 0%, #1a1a2e 100%);
          color: var(--color-text-light);
          text-align: center;
          padding: 6rem 1.5rem;
          position: relative;
          overflow: hidden;
        }
        .user-cta-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background:
            radial-gradient(ellipse 50% 80% at 20% 50%, rgba(196, 30, 58, 0.2) 0%, transparent 50%),
            radial-gradient(ellipse 50% 80% at 80% 50%, rgba(124, 179, 66, 0.15) 0%, transparent 50%);
          animation: gradientShift 15s ease-in-out infinite;
        }
        .user-cta-inner { max-width: 700px; margin: 0 auto; position: relative; z-index: 1; }
        .user-cta-title { font-size: clamp(2rem, 4vw, 2.75rem); font-weight: 700; margin-bottom: 1.25rem; }
        .user-cta-text { font-size: 1.25rem; opacity: 0.9; margin-bottom: 2.5rem; }
        .app-badges { display: flex; flex-wrap: wrap; justify-content: center; gap: 1.25rem; }
        .app-badge-link {
          display: inline-block;
          text-decoration: none;
          transition: all var(--transition);
          border-radius: 8px;
        }
        .app-badge-img {
          height: 58px;
          width: auto;
          display: block;
          border-radius: 8px;
          transition: all var(--transition);
        }
        .app-badge-link:hover .app-badge-img {
          transform: translateY(-3px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.4);
        }
        .app-badge-link:focus {
          outline: 2px solid var(--color-accent);
          outline-offset: 3px;
        }
        .app-note { margin-top: 2rem; font-size: 0.95rem; opacity: 0.8; }

        /* Tagline Banner */
        .tagline-banner {
          background: var(--color-bg-cream);
          text-align: center;
          padding: 4rem 1.5rem;
          position: relative;
        }
        .tagline-text {
          font-size: clamp(1.75rem, 4vw, 2.5rem);
          font-weight: 700;
          color: var(--color-text-dark);
        }
        .tagline-highlight {
          background: linear-gradient(135deg, var(--color-primary), #ff6b6b);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* Highlight Box */
        .highlight-box {
          background: linear-gradient(135deg, var(--color-primary-light), #fff0f3);
          border-left: 4px solid var(--color-primary);
          padding: 2rem;
          border-radius: 0 var(--radius-md) var(--radius-md) 0;
          margin: 2.5rem 0;
          box-shadow: var(--shadow-md);
        }
        .highlight-box p { color: var(--color-text-dark); margin: 0; font-size: 1.15rem; }

        /* Contact Section */
        .contact-section {
          background: var(--color-bg-dark);
          color: var(--color-text-light);
          padding: 6rem 1.5rem;
          position: relative;
        }
        .contact-inner { max-width: 700px; margin: 0 auto; }
        .contact-header { text-align: center; margin-bottom: 3rem; }
        .contact-title { font-size: clamp(2rem, 4vw, 2.5rem); font-weight: 700; margin-bottom: 1rem; }
        .contact-subtitle { font-size: 1.15rem; color: var(--color-text-muted-light); }

        .contact-form {
          background: var(--glass-bg);
          border-radius: var(--radius-lg);
          padding: 3rem;
          border: 1px solid var(--glass-border);
          backdrop-filter: blur(20px);
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
        }
        .form-row { display: grid; grid-template-columns: 1fr; gap: 1.5rem; margin-bottom: 1.5rem; }
        @media (min-width: 600px) { .form-row.two-col { grid-template-columns: 1fr 1fr; } }
        .form-group { display: flex; flex-direction: column; }
        .form-label { font-size: 0.95rem; font-weight: 600; margin-bottom: 0.6rem; color: var(--color-text-light); }
        .form-label .required { color: var(--color-primary); }
        .form-input, .form-textarea, .form-select {
          width: 100%;
          padding: 1rem 1.25rem;
          font-family: var(--font-primary);
          font-size: 1rem;
          color: var(--color-text-light);
          background: rgba(255,255,255,0.08);
          border: 2px solid var(--glass-border);
          border-radius: var(--radius-sm);
          transition: all var(--transition);
        }
        .form-input::placeholder, .form-textarea::placeholder { color: rgba(255,255,255,0.5); }
        .form-input:focus, .form-textarea:focus, .form-select:focus {
          outline: none;
          border-color: var(--color-accent);
          box-shadow: 0 0 0 3px rgba(124, 179, 66, 0.2);
          background: rgba(255,255,255,0.12);
        }
        .form-textarea { min-height: 140px; resize: vertical; }
        .form-select {
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 1rem center;
          background-size: 1.25rem;
          padding-right: 3rem;
        }
        .form-select option { background: var(--color-bg-dark); color: var(--color-text-light); }
        .form-submit { width: 100%; margin-top: 1.25rem; }
        .form-note { text-align: center; margin-top: 1.5rem; font-size: 0.95rem; color: var(--color-text-muted-light); }
        .form-feedback { text-align: center; margin-top: 1rem; padding: 0.75rem 1rem; border-radius: 8px; font-weight: 500; }
        .form-feedback-success { background: rgba(124, 179, 66, 0.15); color: #7cb342; border: 1px solid rgba(124, 179, 66, 0.3); }
        .form-feedback-error { background: rgba(196, 30, 58, 0.15); color: #ef5350; border: 1px solid rgba(196, 30, 58, 0.3); }
        .phone-hint { font-size: 0.85rem; color: var(--color-text-muted-light); margin-top: 0.25rem; }

        /* Footer */
        .footer {
          background: var(--color-bg-dark);
          color: var(--color-text-light);
          padding: 5rem 1.5rem 1.5rem;
          border-top: 1px solid var(--color-border-dark);
        }
        .footer-inner { max-width: 1200px; margin: 0 auto; }
        .footer-top {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          margin-bottom: 2rem;
        }
        @media (min-width: 768px) { .footer-top { grid-template-columns: 2fr 1fr 1fr 1fr; } }
        .footer-logo {
          height: 50px;
          width: auto;
          display: block;
        }
        .footer-brand p { color: var(--color-text-muted-light); font-size: 1rem; max-width: 300px; margin-top: 1.25rem; line-height: 1.75; }
        .footer-heading { font-weight: 600; margin-bottom: 1.25rem; margin-top: 0.5rem; font-size: 1.05rem; }
        .footer-links { list-style: none; }
        .footer-links li { margin-bottom: 0.75rem; }
        .footer-links a {
          color: var(--color-text-muted-light);
          text-decoration: none;
          font-size: 0.95rem;
          transition: all var(--transition);
          position: relative;
        }
        .footer-links a:hover { color: var(--color-text-light); padding-left: 8px; }
        .footer-social {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 1rem;
          padding: 2.5rem 0;
          border-top: 1px solid var(--color-border-dark);
          border-bottom: 1px solid var(--color-border-dark);
          margin-bottom: 2.5rem;
        }
        .footer-social a {
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--glass-bg);
          border: 1px solid var(--glass-border);
          border-radius: var(--radius-sm);
          color: var(--color-text-light);
          transition: all var(--transition);
        }
        .footer-social a:hover {
          background: var(--color-primary);
          border-color: var(--color-primary);
          transform: translateY(-4px) scale(1.05);
          box-shadow: 0 8px 20px rgba(196, 30, 58, 0.3);
        }
        .footer-social svg { width: 22px; height: 22px; }
        .footer-bottom { text-align: center; }
        .footer-copyright { color: var(--color-text-muted-light); font-size: 0.95rem; margin-bottom: 1.25rem; }
        .footer-legal { display: flex; flex-direction: column; align-items: center; gap: 0.6rem; }
        .footer-legal-row { display: flex; flex-wrap: wrap; justify-content: center; gap: 0.5rem 1.75rem; }
        .footer-legal a { color: var(--color-text-muted-light); text-decoration: underline; font-size: 0.9rem; transition: color var(--transition); }
        .footer-legal a:hover { color: var(--color-text-light); }
        .footer-legal a:focus-visible { outline: 2px solid #fff; outline-offset: 3px; border-radius: 2px; }
        .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }

        .visually-hidden { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
        :global(.reading-highlight) { background: rgba(124, 179, 66, 0.3) !important; outline: 2px solid var(--color-accent); }

        /* Reduced Motion Support */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
          :global(html) { scroll-behavior: auto; }
          .hero-gradient { animation: none; }
          .gradient-text { animation: none; }
          :global(.reveal), :global(.reveal-left), :global(.reveal-right), :global(.reveal-scale) { opacity: 1; transform: none; }
          .cursor-glow { display: none; }
          .hero-particles { display: none; }
          .tilt-card, .venue-card, .accessibility-card { transform: none !important; }
        }

        @media print {
          .header, .user-cta-section, .mobile-menu-btn, .contact-section, .a11y-toolbar, .scroll-progress, .grain-overlay, .cursor-glow { display: none; }
          .hero { padding: 2rem 0; min-height: auto; }
        }
      `}</style>
    </>
  );
};

export default Landing;
